import {
  type JSX,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { cx } from './primitives';

/**
 * `Modal` - built on the native `<dialog>` element.
 *
 * `showModal()` gives focus containment, the top layer, Escape-to-dismiss, an
 * `inert` background and a styleable `::backdrop` from the platform. Every one of
 * those is a thing a hand-rolled modal gets subtly wrong, and none of them costs
 * a dependency (tech-stack section 11). The only behaviour added here is the
 * click-outside dismissal, which `<dialog>` deliberately leaves to the caller.
 *
 * Confirmation dialogs on the bakery tablet are the load-bearing case: an
 * operator with floured hands hitting "Reject order" must get a target of at
 * least 60px and must not be able to dismiss it accidentally (PRD BAK-024), so
 * `dismissible={false}` blocks Escape and the backdrop both.
 *
 * Every string is passed in already translated (PRD NFR-006).
 */

/**
 * How wide the panel is allowed to get. `xl` is for a whole FORM in a dialog -
 * the product editor, which lays its fields out in two columns and reads as one
 * screenful only when both of them have room for a control and its label.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalProps {
  open: boolean;
  /**
   * Called for every dismissal route: the close button, Escape, and a click on
   * the backdrop. Ignored entirely when `dismissible` is false.
   */
  onClose: () => void;
  /** Already translated. Rendered as the dialog's accessible name. */
  title: ReactNode;
  /**
   * Keeps the title as the accessible name and stops drawing it, collapsing the
   * header that carried it - for a sheet whose own content already states the
   * title and whose first element has to reach the panel's top edge (the guest
   * item sheet, where the product's name sits under a full-bleed photograph).
   * The dialog is still named: the heading is only visually hidden, never
   * dropped. A close button, when there is one, then floats over that first
   * element instead of sitting in a header of its own.
   */
  titleHidden?: boolean;
  children?: ReactNode;
  /** Footer actions, typically buttons. Rendered right-aligned. */
  actions?: ReactNode;
  size?: ModalSize;
  /**
   * False for a decision the operator must actually make - Escape and the
   * backdrop stop dismissing and the close button is not rendered.
   */
  dismissible?: boolean;
  /** Accessible name for the close button. Already translated; required when shown. */
  closeLabel?: string;
  className?: string;
}

export function Modal({
  open,
  onClose,
  title,
  titleHidden = false,
  children,
  actions,
  size = 'md',
  dismissible = true,
  closeLabel,
  className,
}: ModalProps): JSX.Element {
  const ref = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open) {
      // Re-calling showModal on an open dialog throws; guard on the live state.
      if (!dialog.open) dialog.showModal();
    } else if (dialog.open) {
      dialog.close();
    }
  }, [open]);

  /**
   * The page behind a modal does not move.
   *
   * `showModal()` makes the background INERT, which is about focus and pointer
   * targets - it says nothing about scrolling, and every browser still scrolls
   * the document under an open dialog. On a phone that is what a guest gets for
   * flicking anywhere outside the panel, and what they get for flicking INSIDE
   * it once the body has reached its own end: the gesture chains out to the page
   * and the shelf slides away behind the sheet they are reading. The sheet looks
   * broken, and there is nothing to grab to put it back.
   *
   * Locking `<html>` rather than `<body>` because the reset in these apps leaves
   * the scroll container on the root. The previous value is restored rather than
   * cleared, so a surface that had set its own overflow keeps it, and a COUNTER
   * on the element carries nested dialogs - the payment sheet opening over the
   * basket must not be un-locked by whichever of the two happens to close first.
   * The chaining half is `overscroll-behavior` on the body (tokens.css).
   */
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const depth = Number(root.dataset.cibModalDepth ?? '0');
    if (depth === 0) {
      root.dataset.cibModalPrevOverflow = root.style.overflow;
      root.style.overflow = 'hidden';
    }
    root.dataset.cibModalDepth = String(depth + 1);
    return () => {
      const open_ = Number(root.dataset.cibModalDepth ?? '1') - 1;
      if (open_ <= 0) {
        root.style.overflow = root.dataset.cibModalPrevOverflow ?? '';
        delete root.dataset.cibModalPrevOverflow;
        delete root.dataset.cibModalDepth;
      } else {
        root.dataset.cibModalDepth = String(open_);
      }
    };
  }, [open]);

  /**
   * Escape fires `cancel`. It is always prevented, so the element never closes
   * itself: the `open` prop is the single source of truth and the effect above is
   * the only thing that calls `close()`. Letting the platform close it too would
   * fire `onClose` a second time, and a second time on a "Reject order" dialog is
   * a second rejection.
   */
  const handleCancel = useCallback(
    (event: SyntheticEvent<HTMLDialogElement>) => {
      event.preventDefault();
      if (dismissible) onClose();
    },
    [dismissible, onClose],
  );

  /**
   * A click that lands on the dialog element itself is a click on the backdrop:
   * the panel inside covers the whole content box, so any other click reports a
   * descendant as its target.
   */
  const handleClick = useCallback(
    (event: ReactMouseEvent<HTMLDialogElement>) => {
      if (!dismissible) return;
      if (event.target === ref.current) onClose();
    },
    [dismissible, onClose],
  );

  return (
    // The onClick below is not an action: it only detects a click on the
    // backdrop. Its keyboard equivalent is Escape, which the platform delivers as
    // `cancel` and `handleCancel` above answers - adding an onKeyDown here would
    // make the dialog dismissable by any keypress.
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled by onCancel.
    <dialog
      ref={ref}
      className={cx('cib-modal', `cib-modal--${size}`, className)}
      onCancel={handleCancel}
      onClick={handleClick}
    >
      <div className="cib-modal__panel">
        <header className={cx('cib-modal__header', titleHidden && 'cib-modal__header--bare')}>
          <h2 className={cx('cib-modal__title', titleHidden && 'cib-modal__title--hidden')}>
            {title}
          </h2>
          {dismissible && closeLabel !== undefined ? (
            <button
              type="button"
              className={cx('cib-modal__close', titleHidden && 'cib-modal__close--floating')}
              aria-label={closeLabel}
              onClick={onClose}
            >
              <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : null}
        </header>

        <div className="cib-modal__body">{children}</div>

        {actions !== undefined ? <footer className="cib-modal__actions">{actions}</footer> : null}
      </div>
    </dialog>
  );
}
